'use client'

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChatBubbleOvalLeftEllipsisIcon, CheckIcon } from '@heroicons/react/24/outline'

interface AgencyLogo {
  src: string
  alt: string
  /** Širina kartice, u pikselima */
  width: number
  /** Visina kartice, u pikselima */
  height: number
  /** Razmak oko logotipa unutar kartice, u pikselima */
  padding: number
  /** URL agencije */
  href: string
}

/** Visina svake kartice, u pikselima */
const itemHeight = 80

const agencies: AgencyLogo[] = [
  { src: '/landing/agencies/avio-express.png', alt: 'Avio Express', width: 180, height: 80, padding: 12, href: 'https://www.avioexpress.ba/' },
  { src: '/landing/agencies/rijaset.webp', alt: 'Rijaset - Hadz i Umra', width: 220, height: 100, padding: 4, href: 'https://hadziumra.ba/' },
  { src: '/landing/agencies/ventura-travel.png', alt: 'Ventura Travel', width: 140, height: 110, padding: 0, href: 'https://ventura.travel/' },
  { src: '/landing/agencies/travel-centar.png', alt: 'Travel Centar', width: 160, height: 80, padding: 12, href: 'https://travelcentar.ba/' },
  { src: '/landing/agencies/victorius-travel.svg', alt: 'Victorius Travel', width: 200, height: 80, padding: 8, href: 'https://victorius.ba/' },
  { src: '/landing/agencies/relax-tours.png', alt: 'Relax Tours', width: 110, height: 80, padding: 8, href: 'https://relax-tours.ba/' },
  { src: '/landing/agencies/da-travel.png', alt: 'DA Travel', width: 110, height: 80, padding: 16, href: 'https://datravel.ba/' },
  { src: '/landing/agencies/studentski-pohodi.png', alt: 'Studentski Pohodi', width: 110, height: 100, padding: 8, href: 'https://pohodi.ba/' },
  { src: '/landing/agencies/snow-hunters.webp', alt: 'Snow Hunters', width: 110, height: 100, padding: 8, href: 'https://snowhunters.ba/' },
  { src: '/landing/agencies/vas-bih.png', alt: 'VAS Travel', width: 110, height: 100, padding: 8, href: 'https://vas.ba/' },
  { src: '/landing/agencies/mondaro.png', alt: 'Mondaro', width: 150, height: 100, padding: 4, href: 'https://mondaro.ba/' },
  { src: '/landing/agencies/nusuk.webp', alt: 'Nusuk', width: 150, height: 100, padding: 8, href: 'https://nusuktravel.org/' },
]

const shuffle = <T,>(list: T[]): T[] => {
  const next = [...list]
  for (let i = next.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[next[i], next[j]] = [next[j], next[i]]
  }
  return next
}

const benefits = ['Landing.Agencies.Benefit.Guides', 'Landing.Agencies.Benefit.Discount', 'Landing.Agencies.Benefit.Group', 'Landing.Agencies.Benefit.Install', 'Landing.Agencies.Benefit.Support']

/** Tells the agency's own analytics that the visitor came from our landing page. */
const withReferralUtm = (href: string) => {
  const url = new URL(href)
  url.searchParams.set('utm_source', 'esimfly')
  url.searchParams.set('utm_medium', 'referral')
  url.searchParams.set('utm_campaign', 'partner_agencies')
  return url.toString()
}

/** `hidden` is the duplicated marquee copy, kept out of the tab order. */
const AgencyCard = ({ agency, hidden }: { agency: AgencyLogo; hidden?: boolean }) => (
  <li className='box-border h-20 flex shrink-0 items-center justify-center rounded-lg border border-slate-200/80 bg-white shadow-sm'>
    <a href={withReferralUtm(agency.href)} target='_blank' rel='noopener noreferrer' tabIndex={hidden ? -1 : undefined} data-track-cta={`agency_logo_${agency.alt}`}>
      <img src={agency.src} alt={agency.alt} className='h-full w-full object-contain' style={{ width: agency.width, height: itemHeight, padding: agency.padding }} />
    </a>
  </li>
)

const Agencies = () => {
  const { t } = useTranslation()
  const [items, setItems] = useState(agencies)
  const partnerHref = `https://wa.me/38761603225?text=${encodeURIComponent(t('Landing.Agencies.WhatsAppMessage'))}`

  useEffect(() => {
    setItems(shuffle(agencies))
  }, [])

  return (
    <section className='bg-orange-50 py-8' aria-labelledby='agencies-heading' data-track-section='agencies'>
      <div className='mx-auto max-w-5xl px-4'>
        <div className='text-left md:text-center'>
          <p className='text-[13px] font-semibold uppercase tracking-[0.16em] text-[#F3603C]'>{t('Landing.Agencies.Eyebrow')}</p>
          <h2 id='agencies-heading' className='mt-3 text-3xl font-bold tracking-tight text-blue-950'>
            {t('Landing.Agencies.Heading')}
          </h2>
          <p className='mt-4 max-w-xl text-lg leading-relaxed text-slate-500 md:mx-auto'>{t('Landing.Agencies.Description', { count: agencies.length })}</p>
        </div>

        <div className='-mx-4 mt-6 overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_2rem,black_calc(100%-2rem),transparent)] md:mx-0'>
          <div className='animate-agency-marquee flex w-max hover:[animation-play-state:paused]'>
            {[0, 1].map(copy => (
              <ul key={copy} className='flex shrink-0 items-center gap-4 pr-4' aria-hidden={copy === 1}>
                {items.map(agency => (
                  <AgencyCard agency={agency} hidden={copy === 1} key={`${copy}-${agency.src}`} />
                ))}
              </ul>
            ))}
          </div>
        </div>

        <div className='mt-6 flex flex-col gap-5 rounded-2xl bg-white p-5 md:flex-row md:items-center md:justify-between md:gap-8 md:p-6'>
          <div className='min-w-0'>
            <h3 className='text-lg font-bold text-blue-950'>{t('Landing.Agencies.CtaHeading')}</h3>
            <p className='mt-1 text-[15px] leading-relaxed text-slate-500'>{t('Landing.Agencies.CtaDescription')}</p>
            <ul className='mt-3 grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2'>
              {benefits.map(benefit => (
                <li key={benefit} className='flex items-center gap-2 text-sm font-medium text-blue-950'>
                  <CheckIcon className='size-4 shrink-0 text-[#F3603C]' strokeWidth={3} aria-hidden />
                  {t(benefit)}
                </li>
              ))}
            </ul>
          </div>
          <a
            href={partnerHref}
            data-track-cta='whatsapp_agencies'
            target='_blank'
            rel='noopener noreferrer'
            className='inline-flex min-h-12 shrink-0 items-center justify-center gap-2 rounded-xl bg-[#0F7A40] px-5 text-base font-bold text-white transition-colors hover:bg-[#0c6535]'
          >
            <ChatBubbleOvalLeftEllipsisIcon className='size-5' aria-hidden />
            {t('Landing.Agencies.CtaAction')}
          </a>
        </div>
      </div>
    </section>
  )
}

export default Agencies
